import React, { useState } from "react";
import {
    Grid,
    RadioGroup,
    Typography,
    FormControlLabel,
    Radio,
    FormControl,
    MenuItem,
    InputLabel,
    Select,
    FormHelperText,
} from "@mui/material";
import { useFormikContext } from "formik";

//contains 5 questions (0, A, B, C, D)
//possibly flows:
//0 - Yes => A - Working fewer hours = > B
//0 - Yes => A - On paid sick leave = > B & C
//0 - Yes => A - On unpaid sick leave = > B & C
//0 - No => D

const questionCGrid = {
    padding: "10px 20px 0px 0px",
};

const questionCHeader = {
    fontSize: "1.5rem",
};

const questionContainer = {
    marginBottom: "80px",
};

const center = {
    display: "flex",
    justifyContent: "center",
};

const WorkStatusQuestions = ({
    id,
    name,
    className,
    questions,
    options,
    subQuestionInfo,
    questionCText,
    ...otherProps
}) => {
    const formik = useFormikContext();
    const months = [];
    const years = [];

    const [answers, setAnswers] = useState({
        zero: "",
        a: "",
        b: "",
        cYears: "",
        cMonths: "",
        d: "",
        errors: ["zero"],
    });
    const [displaying, setDisplaying] = useState({ zero: true, a: false, b: false, c: false, d: false });
    const keys = Object.keys(options);

    for (let i = 1; i <= 11; i++) {
        months.push(i);
    }

    for (let i = 1; i <= 100; i++) {
        years.push(i);
    }

    const handleChange = (event, id) => {
        let answersJson = { ...answers, [id]: event.target.value };

        let displayingJson = { ...displaying };
        switch (id) {
            case "zero":
                if (event.target.value === options.zero.options[0]) {
                    displayingJson = { ...displaying, a: true, b: false, c: false, d: false };
                } else {
                    displayingJson = { ...displaying, a: false, b: false, c: false, d: true };
                }

                break;
            case "a":
                if (event.target.value === options.a.options[0]) {
                    displayingJson = { ...displaying, b: false, c: false, d: false };
                } else if (event.target.value === options.a.options[1]) {
                    displayingJson = { ...displaying, b: true, c: false, d: false };
                } else if (event.target.value === options.a.options[2]) {
                    displayingJson = { ...displaying, b: true, c: true, d: false };
                } else if (event.target.value === options.a.options[3]) {
                    displayingJson = { ...displaying, b: true, c: true, d: false };
                }
                break;
        }
        //dont add value if id is c as it will be cMonths or cYears

        let displayingKeys = Object.keys(displayingJson);

        let errors = [];
        displayingKeys.forEach((key) => {
            //reset answers that are not being displayed
            if (!displayingJson[key]) {
                if (key === "c") {
                    answersJson = { ...answersJson, cYears: "", cMonths: "" };
                } else {
                    answersJson = { ...answersJson, [key]: "" };
                }
            }
            //if being displayed then check if there is an answer
            else {
                if (answersJson[key] === "") {
                    errors.push(key);
                } else if (key === "c") {
                    if (answersJson["cYears"] === "" || answersJson["cMonths"] === "") {
                        errors.push("cYears");
                        errors.push("cMonths");
                    }
                }
            }
        });

        //set error value
        answersJson = { ...answersJson, errors: errors };

        setAnswers(answersJson);
        setDisplaying(displayingJson);
        formik.setFieldValue(name, answersJson);
    };

    return keys.map((questionOptionsKey, i) => {
        if (questionOptionsKey != "c") {
            return (
                <>
                    {displaying[questionOptionsKey] && (
                        <Grid container sx={questionContainer}>
                            <Grid item xs={11}>
                                <Typography variant={"h6"} htmlFor={id} tabIndex="0">
                                    {questions[questionOptionsKey]}
                                </Typography>
                            </Grid>
                            <Grid item xs={11}>
                                <RadioGroup
                                    name="RadioButton"
                                    value={answers[questionOptionsKey]}
                                    onChange={(e) => handleChange(e, questionOptionsKey)}
                                    {...otherProps}
                                >
                                    {options[questionOptionsKey].options.map((option, j) => (
                                        <FormControlLabel
                                            value={option}
                                            control={<Radio aria-label={"radio button"} tabIndex="0" />}
                                            label={
                                                <span
                                                    style={{
                                                        color:
                                                            answers.errors.includes(questionOptionsKey) &&
                                                            formik.errors[name]
                                                                ? "#f44336"
                                                                : null,
                                                    }}
                                                >
                                                    {option}
                                                </span>
                                            }
                                            tabIndex="0"
                                        />
                                    ))}

                                    {answers.errors.includes(questionOptionsKey) ? (
                                        <FormHelperText error={true} style={{ color: "#f44336" }}>
                                            {formik.errors[name] ? formik.errors[name].errors : null}
                                        </FormHelperText>
                                    ) : null}
                                </RadioGroup>
                            </Grid>
                        </Grid>
                    )}
                </>
            );
        } else {
            return (
                <>
                    {displaying[questionOptionsKey] && (
                        <Grid container sx={questionContainer}>
                            <Grid item xs={11}>
                                <Typography variant={"h6"} htmlFor={id} tabIndex="0">
                                    {questions[questionOptionsKey]}
                                </Typography>
                            </Grid>
                            <Grid item xs={11}>
                                <Grid container>
                                    <Grid item xs="12" md="3" sx={{ ...questionCGrid, ...center }}>
                                        <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
                                            <InputLabel id="yearsLbl">{questionCText["Years"]}</InputLabel>
                                            <Select
                                                labelId="yearsLbl"
                                                value={answers.cYears}
                                                label="Years"
                                                MenuProps={{ sx: { maxHeight: 300 } }}
                                                onChange={(e) => handleChange(e, "cYears")}
                                            >
                                                {years.map((year, i) => (
                                                    <MenuItem value={year}>{year}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs="12" sm="3" sx={{ ...questionCGrid, ...center }}>
                                        <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
                                            <InputLabel id="monthsLbl">{questionCText["Months"]}</InputLabel>
                                            <Select
                                                labelId="monthsLbl"
                                                value={answers.cMonths}
                                                label="Months"
                                                MenuProps={{ sx: { maxHeight: 300 } }}
                                                onChange={(e) => handleChange(e, "cMonths")}
                                            >
                                                {months.map((month, i) => (
                                                    <MenuItem value={month}>{month}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item sx="0" md="6" />
                                    <Grid item xs="12" sm="6" sx={center}>
                                        {answers.errors.includes("cMonths") || answers.errors.includes("cYears") ? (
                                            <FormHelperText error={true} style={{ color: "#f44336" }}>
                                                {formik.errors[name] ? formik.errors[name].errors : null}
                                            </FormHelperText>
                                        ) : null}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </>
            );
        }
    });
};

export default WorkStatusQuestions;
